import * as React from 'react';

import { useEventCallback, useResizeObserver } from '@gitbook/util-react-hooks';

interface UseCoverPositionOptions {
    /** A ref to the image element */
    imgRef: React.RefObject<HTMLImageElement | null>;
    /** The initial position of the cover image in pixels */
    value: number;
    /** Whether the cover image is being repositioned */
    repositioning: boolean;
    /** A callback to commit the final position of the cover image */
    onChange: (y: number) => void;
}

interface UseCoverPositionResult {
    /** Ref callback for the container element */
    containerRef: (element: HTMLDivElement | null) => void;
    /** The object position Y percentage */
    objectPositionY: number;
    /** Human-readable position label */
    positionLabel: string;
    /** Callback to set image dimensions */
    setImageDimensions: (dimensions: { width: number; height: number }) => void;
}

/**
 * Hook to handle the repositioning of the cover image, returns:
 * - `containerRef`: a callback ref to attach to the container element
 * - `objectPositionY`: the object position percentage
 * - `positionLabel`: the position label
 * - `setImageDimensions`: a callback to set the image dimensions
 */
export function useCoverPosition(options: UseCoverPositionOptions): UseCoverPositionResult {
    const { imgRef, value, repositioning } = options;

    const onChange = useEventCallback(options.onChange);

    const containerElementRef = React.useRef<HTMLDivElement | null>(null);

    const [imageDimensions, setImageDimensions] = React.useState<{
        width: number;
        height: number;
    } | null>(null);
    const [containerDimensions, setContainerDimensions] = React.useState<{
        width: number;
        height: number;
    } | null>(null);

    const draggingRef = React.useRef(false);
    const activePointerIdRef = React.useRef<number | null>(null);
    const startYRef = React.useRef(0);
    const startOffsetRef = React.useRef(0);
    const animationFrameIdRef = React.useRef<number | null>(null);

    const resizeObserverRef = useResizeObserver((entry) => {
        const newWidth = entry.contentRect.width;
        const newHeight = entry.contentRect.height;
        setContainerDimensions((prev) => {
            // Only update if dimensions actually changed to prevent unnecessary re-renders
            if (prev && prev.width === newWidth && prev.height === newHeight) {
                return prev;
            }
            return { width: newWidth, height: newHeight };
        });
    });

    // Merge the internal ref with the resize observer ref
    const mergedContainerRef = React.useCallback(
        (element: HTMLDivElement | null) => {
            containerElementRef.current = element;
            resizeObserverRef(element);
        },
        [resizeObserverRef]
    );

    const ratio =
        imageDimensions && containerDimensions
            ? Math.max(
                  containerDimensions.width / imageDimensions.width,
                  containerDimensions.height / imageDimensions.height
              )
            : 1;
    const safeRatio = ratio || 1;

    const scaledHeight =
        imageDimensions && containerDimensions ? imageDimensions.height * safeRatio : null;
    const maxOffset =
        scaledHeight && containerDimensions
            ? Math.max(0, (scaledHeight - containerDimensions.height) / 2 / safeRatio)
            : 0;

    // Parse the position between the allowed min/max
    const clampedObjectPositionY = React.useCallback(
        (offset: number): number => {
            if (!containerDimensions || !imageDimensions) {
                return 50;
            }

            const scaled = imageDimensions.height * safeRatio;
            if (scaled <= containerDimensions.height || maxOffset === 0) {
                return 50;
            }

            const clampedOffset = Math.max(-maxOffset, Math.min(maxOffset, offset));
            const relative = (maxOffset - clampedOffset) / (2 * maxOffset);
            return relative * 100;
        },
        [containerDimensions, imageDimensions, maxOffset, safeRatio]
    );

    // Handle pointer move and pointer up during repositioning
    React.useEffect(() => {
        if (!repositioning) {
            return;
        }

        const handleMove = (event: PointerEvent) => {
            if (
                !draggingRef.current ||
                !imgRef.current ||
                !containerDimensions ||
                !imageDimensions ||
                activePointerIdRef.current !== event.pointerId
            ) {
                return;
            }

            const delta = event.clientY - startYRef.current;
            const newOffset = startOffsetRef.current + delta / safeRatio;
            const clamped = Math.max(-maxOffset, Math.min(maxOffset, newOffset));

            // Calculate the new image position
            const positionY = clampedObjectPositionY(clamped);

            // Directly update the DOM style for immediate visual feedback
            imgRef.current.style.objectPosition = `50% ${positionY.toString()}%`;

            // Update state in batches using requestAnimationFrame
            if (animationFrameIdRef.current !== null) {
                cancelAnimationFrame(animationFrameIdRef.current);
            }
            animationFrameIdRef.current = requestAnimationFrame(() => {
                animationFrameIdRef.current = null;
                onChange(clamped);
            });
        };

        const finalizePointer = (event: PointerEvent) => {
            if (activePointerIdRef.current !== event.pointerId) {
                return;
            }

            draggingRef.current = false;
            const element = containerElementRef.current;
            if (element?.hasPointerCapture(event.pointerId)) {
                element.releasePointerCapture(event.pointerId);
            }
            activePointerIdRef.current = null;

            if (animationFrameIdRef.current !== null) {
                cancelAnimationFrame(animationFrameIdRef.current);
                animationFrameIdRef.current = null;
            }
        };

        window.addEventListener('pointermove', handleMove);
        window.addEventListener('pointerup', finalizePointer);
        window.addEventListener('pointercancel', finalizePointer);

        return () => {
            window.removeEventListener('pointermove', handleMove);
            window.removeEventListener('pointerup', finalizePointer);
            window.removeEventListener('pointercancel', finalizePointer);
            if (animationFrameIdRef.current !== null) {
                cancelAnimationFrame(animationFrameIdRef.current);
                animationFrameIdRef.current = null;
            }
        };
    }, [
        repositioning,
        imgRef,
        containerDimensions,
        imageDimensions,
        safeRatio,
        maxOffset,
        clampedObjectPositionY,
    ]);

    // Clamp position when maxOffset changes (e.g., when resizing or image loads)
    React.useEffect(() => {
        if (!imageDimensions || !containerDimensions) {
            return;
        }

        const clamped = Math.max(-maxOffset, Math.min(maxOffset, value));
        if (clamped !== value) {
            onChange(clamped);
        }
    }, [maxOffset, imageDimensions, containerDimensions, value]);

    // Effect for pointerdown on container
    React.useEffect(() => {
        const element = containerElementRef.current;
        if (!element || !repositioning) {
            return;
        }

        const handlePointerDown = (event: PointerEvent) => {
            event.preventDefault();
            draggingRef.current = true;
            startYRef.current = event.clientY;
            startOffsetRef.current = value;
            activePointerIdRef.current = event.pointerId;
            element.setPointerCapture(event.pointerId);
        };

        element.addEventListener('pointerdown', handlePointerDown);
        return () => {
            element.removeEventListener('pointerdown', handlePointerDown);
        };
    }, [repositioning, value]);

    const objectPositionY = clampedObjectPositionY(value);

    return {
        containerRef: mergedContainerRef,
        objectPositionY,
        positionLabel: getPositionLabel(objectPositionY),
        setImageDimensions,
    };
}

function getPositionLabel(y: number): string {
    switch (y) {
        case 0:
            return 'Image is at top';
        case 50:
            return 'Image centered';
        case 100:
            return 'Image is at bottom';
        default:
            return `${Number(y).toFixed(0)}%`;
    }
}
